<template>
  <div class="container url-attributes">
    <div class="url-attributes__title-container">
      <div class="url-attributes__title-left">
        <div class="url-attributes__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="url-attributes__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="url-attributes__cross">&times;</span>
          </button>
        </div>
      </div>
      <div v-if="isDataFetched" class="url-attributes__title-right">
        <div class="url-attributes__urls">
          <span class="url-attributes__count">Attributes: {{totalItems}}</span>
          <base-select
            class="url-attributes__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button @click.prevent="createNew" class="url-attributes__button">
          <base-material-design-icon class="url-attributes__button__plus" icon="plus"/>
          CREATE NEW
        </button>
      </div>
    </div>

    <div v-if="!isDataFetched">
      <div class="url-attributes__no-data">Awaiting Data</div>
    </div>
    <template v-else>
      <div class="common-table url-attributes__table">
        <div class="common-table__row common-table__header">
          <div class="common-table__header-cell"
               v-for="column in tableConfig"
               :key="column.property"
               :class="[`url-attributes__${column.property}`]">
            <base-multi-select-sortable-header
              class="url-attributes__select-header"
              :column="column"
              :sort-config="sortConfig"
              :predefinedOptions="filterParams[column.property]"
              :predefinedNumberFilter="['associated_urls'].includes(column.property) ? filterParams[column.property] : null"
              @sort="onHeaderCellClicked(column, $event)"
              @apply="filterColumn($event, column)"
              @cancel="cancelFilter($event, column)"
              :ref="sortableHeaderRefs[column.property]"
            />
          </div>
        </div>

        <template v-if="dataMissing">
          <div class="common-table__row no-data-block">
            No records found.
          </div>
        </template>
        <template v-else>
          <template v-for="urlAttribute in paginatedUrlAttributes">
            <div :key="urlAttribute._id" class="common-table__row">
              <div class="common-table__cell"
                   v-for="column in tableConfig"
                   :key="column.property"
                   :class="[`url-attributes__${column.property}`]">
                <base-select v-if="column.property === 'is_active'"
                             :options="selectOptions"
                             v-model="urlAttribute.is_active"
                             borderLess
                             alignLeft
                             shiftLabelRight
                             :selectStyle="{ width: '90px !important', height: '62px !important' }"
                             :titleStyle="{ color: '#011627' }"
                             @change="changeUrlAttribute($event, urlAttribute)"/>
                <input v-else-if="column.property === 'name'"
                       :class="[
                         'url-attributes__name_input',
                         {
                           'url-attributes__name_input_err': errorInputIds.includes(urlAttribute._id)
                         }
                       ]"
                       v-model="urlAttribute.name"
                       type="text"
                       @focus="removeIdFromErrors(urlAttribute._id)"
                       @blur="changeUrlAttribute($event, urlAttribute)"
                       @input="() => { urlAttribute.name = urlAttribute.name.trim().replace(/ +/g, '-').replace(/_+/g, '-') }"
                       @keyup.enter="($event) => { $event.target.blur() }"/>
                <template v-else-if="column.property === 'associated_urls'">
                  <span @click="urlCensusLink(urlAttribute.name)" class="url-attributes__attribute_link">{{ urlAttribute.associated_urls }}</span>
                  <base-material-design-icon @click="showConfirmModal(urlAttribute._id)"
                                             class="url-attributes__row-button-cross"
                                             icon="plus"/>
                </template>
              </div>
            </div>
          </template>
        </template>
      </div>
    </template>

    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="url-attributes__update-loader" v-if="isDataUpdating">
      <base-loader class="url-attributes__update-loader-ring"/>
    </div>
    <modal class="url-attributes__confirm-modal" name="confirm-modal" v-bind="confirmModalProps">
      <base-confirm-modal title="Are you sure you want to delete this?"
                          subtitle="This URL Attribute will be removed permanently!"
                          @confirm="confirmModal" />
    </modal>
    <modal name="edit-modal" v-bind="modalProps">
      <AddNew :selectOptions="selectOptions"
              @close="closeAddNewModal"/>
    </modal>
  </div>
</template>

<script>
import BaseSortMixins from '@/common/BaseSortMixins'
import { mapActions, mapState } from 'vuex'
import helper from '@/services/Helper'
import AddNew from '@/features/url-attributes/AddNew'
import baseModalProps from '@/common/BaseModalProps'
import _ from 'lodash'

const ITEMS_PER_PAGE = 100
const DEFAULT_FILTER_PARAMS = {
  name: '',
  is_active: [],
  associated_urls: {
    operator: null,
    value: null
  },
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
const SELECT_OPTIONS = [
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]
// table config
const DEFAULT_CONFIG = [
  {
    label: 'Attribute Name',
    sortByLenght: false,
    property: 'name'
  },
  {
    label: 'Is Active',
    property: 'is_active',
    isSmartInput: false,
    isSelectableValue: true, // this value could be selected using BaseSelect widget
    options: [
      {
        label: 'Yes',
        value: 'yes'
      },
      {
        label: 'No',
        value: 'no'
      }
    ]
  },
  {
    label: 'Associated URLs',
    property: 'associated_urls',
    isNumber: true
  }
]
export default {
  name: 'index',
  components: { AddNew },
  mixins: [BaseSortMixins],
  created () {
    window.scrollTo(0, 0)
  },
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      removeUrlAttributeId: null,
      errorInputIds: [],
      selectOptions: [...SELECT_OPTIONS],
      paginationOptions: [
        {
          label: '100',
          value: 100
        },
        {
          label: '250',
          value: 250
        },
        {
          label: '500',
          value: 500
        }
      ],
      pageItemsLimit: ITEMS_PER_PAGE,
      filterParams: { ...DEFAULT_FILTER_PARAMS },
      tableConfig: [...DEFAULT_CONFIG],
      sortableHeaderRefs: {
        'name': 'name',
        'is_active': 'is_active',
        'associated_urls': 'associated_urls'
      }
    }
  },
  watch: {
    clientId: {
      handler (clientId) {
        this.loadData(clientId)
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('urlAttributes', ['urlAttributes']),
    filteredUrlAttributes () {
      let urlAttributes = [...this.urlAttributes]
      const urlsFilter = ({ operator, value }, count) => {
        value = Number.parseInt(value)
        switch (operator) {
          case 'equal':
            return value === count
          case 'ne':
            return value !== count
          case 'gt':
            return value < count
          case 'lt':
            return value > count
          case 'gte':
            return value <= count
          case 'lte':
            return value >= count
        }
      }
      // eslint-disable-next-line camelcase
      const { associated_urls, name, is_active } = this.filterParams
      return urlAttributes.filter(urlAttribute => {
        const isActiveFilter = !is_active.length || is_active.map(v => v === 'yes' ? 1 : 0).includes(urlAttribute.is_active)
        const nameFilter = (typeof name !== 'string' || !urlAttribute.name) || urlAttribute.name.toLowerCase().includes(name.toLowerCase())
        // eslint-disable-next-line camelcase
        const associatedUrlFilter = (!associated_urls || !associated_urls.operator) || urlsFilter(associated_urls, urlAttribute.associated_urls)

        return isActiveFilter && nameFilter && associatedUrlFilter
      })
    },
    totalItems () {
      return this.filteredUrlAttributes.length
    },
    paginatedUrlAttributes () {
      // eslint-disable-next-line camelcase
      const { page_from, page_to } = this.filterParams
      return helper.sortByMultipleParams(this.filteredUrlAttributes, this.sortConfig).slice(page_from, page_to)
    },
    confirmModalProps () {
      return { ...baseModalProps, height: 'auto', width: '510px' }
    },
    dataMissing () {
      return this.isDataFetched && !this.urlAttributes.length
    },
    modalProps () {
      return { ...baseModalProps, height: 'auto', width: '610px' }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    }
  },
  methods: {
    ...mapActions('urlAttributes', ['getUrlAttributes', 'updateUrlAttributes', 'removeUrlAttribute']),
    ...mapActions('keywordCensus', ['postTableView']),
    async loadData (clientId) {
      if (!clientId) {
        return
      }

      this.isDataUpdating = true
      await this.getUrlAttributes(clientId)
      this.isDataUpdating = false
      this.isDataFetched = true
    },
    async urlCensusLink (attribute) {
      const form = {
        'filters': [{
          'field': 'url_attribute',
          'type': 'in',
          'value': [attribute]
        }]
      }
      const data = await this.postTableView({ form: form, clientId: this.clientId, type: 'url_census' })
      const routeData = this.$router.resolve({ path: `/strategy/url-census/${data.data}` });
      window.open(routeData.href, '_blank');
    },
    createNew () {
      this.$modal.show('edit-modal')
    },
    closeAddNewModal (isUpdate) {
      this.$modal.hide('edit-modal')
      isUpdate && this.loadData(this.clientId)
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    onHeaderCellClicked (cell, direction) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortByLenght = cell.sortByLenght
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
    },
    filterColumn (event, column) {
      if (['associated_urls'].includes(column.property)) {
        this.filterParams[column.property] = {
          value: event.value,
          operator: event.operator
        }
      } else {
        this.filterParams[column.property] = event
        this.filterParams.page_from = 0
        this.filterParams.page_to = this.pageItemsLimit
      }
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
    },
    showConfirmModal (id) {
      this.removeUrlAttributeId = id
      this.$modal.show('confirm-modal')
    },
    confirmModal (status) {
      this.$modal.hide('confirm-modal')
      if (status) {
        this.deleteUrlAttribute()
      }
    },
    async deleteUrlAttribute () {
      this.isDataUpdating = true
      await this.removeUrlAttribute({ id: this.removeUrlAttributeId, client_id: this.clientId })
      await this.loadData(this.clientId)
      this.isDataUpdating = false
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(DEFAULT_FILTER_PARAMS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
    },
    removeIdFromErrors (id) {
      const index = this.errorInputIds.indexOf(id)
      if (index > -1) {
        this.errorInputIds.splice(index, 1)
      }
    },
    async changeUrlAttribute  (e, urlAttribute) {
      if (!urlAttribute.name.length) {
        this.errorInputIds.push(urlAttribute._id)
        return
      } else {
        this.removeIdFromErrors(urlAttribute._id)
      }
      const isUpdated = await this.updateUrlAttributes({ ...urlAttribute, client_id: this.clientId })
      !isUpdated && this.errorInputIds.push(urlAttribute._id)
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    }
  }
}
</script>

<style lang="scss" scoped>
.common-table {
  border-radius: 4px;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: auto;
    padding: 23px 0 23px 13px;
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }

    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.url-attributes {
  position: relative;
  min-height: 200vh !important;

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__associated_urls {
    position: relative;
  }

  &__attribute_link {
    cursor: pointer;
    color: $rfx-bright-blue !important;

    &:hover {
      text-decoration: underline;
    }
  }

  &__row-button-cross {
    cursor: pointer;
    font-size: 26px;
    position: absolute;
    right: 10px;
    transform: rotate(45deg);
  }

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .url-attributes__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__confirm-modal {
    background-color: transparent !important;
  }

  &__is_active {
    padding-bottom: 0;
    padding-top: 0;

    &.common-table__header-cell {
      flex: 0 0 32.5%;
    }
  }

  &__button {
    width: 136px;
    padding-left: 40px;
    padding-right: 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
    position: relative;

    &__plus {
      position: absolute;
      left: 18px;
      top: 23px;
      font-size: 15px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__urls {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    margin-right: 30px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  .pagination-wrapper {
    display: flex;
    height: 60px;
    margin-top: 10px;
    justify-content: center;
    align-items: center;
    border: 1px solid $rfx-light-steel;
    border-radius: 4px;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__name_input {
    width: 100%;
    font-size: 14px;
    padding: 21px 0 21px 25px;
    border: 2px solid transparent;

    &:hover {
      background-color: rgba(40, 74, 255, .1);
    }

    &:focus {
      outline: none;
      border-bottom: 2px solid $rfx-bright-blue;
      background-color: rgba(40, 74, 255, .1);
    }

    &_err {
      border: 2px solid $rfx-light-red;
      color: $rfx-light-red;
    }
  }

  &__name {
    padding: 0 !important;

    &.common-table__header-cell {
      flex: 0 0 32.8%;

      &::v-deep .multi-select__title-container {
        padding-left: 26px;
      }
    }
  }

  &__no-data {
    display: flex;
    height: 300px;
    background-color: #fff;
    border-radius: $rfx-default-border-radius;
    align-items: center;
    justify-content: center;
    font-size: 27px;
    font-weight: 600;
    color: $rfx-medium-grey;
    flex: 1 1;
  }
}
</style>
